// Formatting shared across the fleet pages (cars, lessors, riders, shops,
// facilities). Private to this product — see components/products/README.
//
// AN ABSENT VALUE READS AS AN EM DASH, NEVER AS ZERO OR TODAY. "we don't know
// when this car's qual is due" and "it's due today" are different answers, and
// a fleet page that blurs them sends someone to test the wrong car.

export const EMPTY = "—"

/** "Apr 1, 2029". Date-only columns — no time, no zone shifting. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY
  // Date-only strings ("2029-04-01") parse as UTC midnight, which renders as
  // the PREVIOUS day west of Greenwich. Split the parts and build a local date
  // so a due date shows the day that was actually stored.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const parsed = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value)
  if (Number.isNaN(parsed.getTime())) return EMPTY
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

/** "Apr 1, 2029, 2:30 PM". For audit trails, where the time is the point. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return EMPTY
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** Whole months from today until `value`; negative when already past. */
export function monthsUntil(value: string | null | undefined): number | null {
  if (!value) return null
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const target = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value)
  if (Number.isNaN(target.getTime())) return null
  const today = new Date()
  return (
    (target.getFullYear() - today.getFullYear()) * 12 +
    (target.getMonth() - today.getMonth())
  )
}

/** "12 days" / "1 day". nil in, em dash out — never "0 days". */
export function formatDays(value: number | null | undefined): string {
  if (value == null) return EMPTY
  return `${value} ${value === 1 ? "day" : "days"}`
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return EMPTY
  return value.toLocaleString()
}

/** "general_purpose" -> "General purpose". */
export function humanize(value: string | null | undefined): string {
  if (!value) return EMPTY
  const spaced = value.replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
